/* ============================================================
   usePdfViewer.ts — pdf.js(v6) 비동기 렌더 캡슐화
   원본: design-prototypes/js/landing.js 의 PDF.js 렌더러 블록
     (loadPdfJs / loadPdfReport / renderPdfPage / setupPdfScrollSync)

   핵심 책임:
   · pdfjs-dist 를 npm 의존성으로 사용(CDN 동적 import 제거).
   · **legacy 빌드**(`pdfjs-dist/legacy/build`)를 쓴다(066 research R9). 비-legacy 빌드는
     `Promise.try`(Chrome 128+)·`Promise.withResolvers` 를 폴리필 없이 쓰므로 그 아래
     브라우저(삼성 인터넷 26 이하 등)에서 main↔worker 핸드셰이크가 TypeError 로 죽는다.
     legacy 빌드는 폴리필을 동봉해 Chrome 125+ / Safari 18+ / Firefox ESR+ 를 지원한다
     (제조사 브라우저는 Chromium 버전 기준 환산). 그 아래는 뷰어 대상 외 — 표지 카드는
     서버 생성 이미지라 브라우저와 무관하다.
   · worker 는 Vite 번들 방식(`?url`)으로 로드.
   · pdfLoadToken 패턴: 진행 중 로드를 토큰으로 무효화(탭 전환·열수 변경·unmount 취소).
   · IntersectionObserver 로 현재 페이지 동기화, 타이머·observer·렌더태스크 전량 정리.

   사이드이펙트(observer·렌더태스크·문서)는 cleanup()/onBeforeUnmount 에서 정리 → 누수 0.
   ============================================================ */

import { onBeforeUnmount, ref, shallowRef } from 'vue';
// 타입만 정적 import(런타임 코드 0 — 빌드 시 소거). 실 모듈은 load() 에서 동적 import 한다.
import type * as PdfjsLib from 'pdfjs-dist';
import type { PDFDocumentLoadingTask, PDFDocumentProxy, RenderTask } from 'pdfjs-dist';

/**
 * pdfjs-dist 지연 로더 — 브라우저에서 처음 PDF 를 로드할 때만 모듈을 가져온다.
 *
 * pdfjs 는 모듈 평가 시 DOMMatrix 등 브라우저 전역에 의존한다. 정적 import 하면 SSG(빌드 타임 SSR)
 * 환경에서 모듈 평가만으로 ReferenceError 가 난다(홈·가이드 프리렌더가 통째로 실패). 동적 import 로
 * 클라이언트 첫 호출까지 미뤄 SSR 안전성과 런타임 동작을 모두 만족시킨다. 결과는 1회만 초기화해 캐싱.
 */
let pdfjsPromise: Promise<typeof PdfjsLib> | null = null;
function loadPdfjs(): Promise<typeof PdfjsLib> {
  if (!pdfjsPromise) {
    pdfjsPromise = (async () => {
      // legacy 경로 고정(R9). 타입은 `pdfjs-dist` 정적 import 를 그대로 쓴다(legacy 도 같은 d.mts).
      const lib = await import('pdfjs-dist/legacy/build/pdf.mjs');
      const { default: workerUrl } = await import('pdfjs-dist/legacy/build/pdf.worker.min.mjs?url');
      // worker 를 Vite 번들 URL 로 지정(CDN 의존 제거).
      lib.GlobalWorkerOptions.workerSrc = workerUrl;
      return lib;
    })();
  }
  return pdfjsPromise;
}

/** 렌더된 PDF 페이지 1장의 표시용 메타(canvas 데이터 URL + 기준 크기) */
export interface PdfRenderedPage {
  /** 0-base 페이지 인덱스 */
  readonly index: number;
  /** 1-base 페이지 번호 */
  readonly pageNumber: number;
  /** 본문 canvas 이미지(devicePixelRatio 반영 고해상도) */
  readonly dataUrl: string;
  /** 썸네일 canvas 이미지 */
  readonly thumbUrl: string;
  /** zoom=1 기준 표시 폭(px) — 줌 배율은 컴포넌트가 곱한다 */
  readonly baseW: number;
  /** zoom=1 기준 표시 높이(px) */
  readonly baseH: number;
}

export interface UsePdfViewerOptions {
  /** 1단 보기 기준 폭(px). 원본 520 */
  readonly oneColWidth?: number;
  /** 2단 보기 기준 폭(px). 원본 340 */
  readonly twoColWidth?: number;
  /** 썸네일 폭(px). 원본 104 */
  readonly thumbWidth?: number;
}

export function usePdfViewer(options: UsePdfViewerOptions = {}) {
  const oneColWidth = options.oneColWidth ?? 520;
  const twoColWidth = options.twoColWidth ?? 340;
  const thumbWidth = options.thumbWidth ?? 104;

  /** 렌더 완료 페이지 목록 */
  const pages = shallowRef<PdfRenderedPage[]>([]);
  /** 로딩 진행 상태 */
  const loading = ref(false);
  /** 로딩 실패 메시지(없으면 null) */
  const error = ref<string | null>(null);
  /** 총 페이지 수(미확정이면 null) */
  const totalPages = ref<number | null>(null);
  /** 현재 보이는 페이지(1-base) */
  const currentPage = ref(1);

  /** 진행 중 로드 무효화 토큰 — 증가 시 이전 비동기 콜백 전부 폐기 */
  let token = 0;
  /** 현재 살아있는 로딩 태스크(취소 시 destroy → 워커·문서 정리) */
  let activeTask: PDFDocumentLoadingTask | null = null;
  /** 진행 중 렌더 태스크(취소 대상) */
  let activeRenderTasks: RenderTask[] = [];
  /** 스크롤 싱크 옵저버 */
  let observer: IntersectionObserver | null = null;

  function disposeObserver() {
    if (observer) {
      observer.disconnect();
      observer = null;
    }
  }

  function cancelRenderTasks() {
    for (const task of activeRenderTasks) {
      try {
        task.cancel();
      } catch {
        /* 이미 완료/취소된 태스크는 무시 */
      }
    }
    activeRenderTasks = [];
  }

  async function destroyActiveDoc() {
    disposeObserver();
    cancelRenderTasks();
    if (activeTask) {
      const task = activeTask;
      activeTask = null;
      try {
        // destroy 는 네트워크 중단 + 워커·문서 해제(loadingTask 소관)
        await task.destroy();
      } catch {
        /* destroy 실패는 치명적이지 않음 */
      }
    }
  }

  /**
   * 한 페이지를 canvas 로 렌더해 dataURL·썸네일 포함 메타 반환.
   * 토큰이 어긋나면 즉시 폐기(취소된 로드의 잔여 렌더 방지).
   */
  async function renderPage(
    doc: PDFDocumentProxy,
    pageNumber: number,
    cols: number,
    myToken: number,
    /** 실제 렌더 폭(px). 뷰어 fit-to-width 용 — 미지정 시 cols 기본 상수 사용(하위호환). */
    overrideW?: number,
  ): Promise<PdfRenderedPage | null> {
    const page = await doc.getPage(pageNumber);
    if (myToken !== token) return null;

    const targetW = overrideW ?? (cols === 1 ? oneColWidth : twoColWidth);
    const vp1 = page.getViewport({ scale: 1 });
    const scale = targetW / vp1.width;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const vp = page.getViewport({ scale: scale * dpr });

    const baseW = targetW;
    const baseH = (targetW / vp1.width) * vp1.height;

    // canvas 를 Math.ceil 로 잡으면 뷰포트보다 최대 1px 커지고(예: 1000×1414.285 → 1001×1415),
    // pdf.js 는 그 남는 1px 을 페이지 배경(흰색)으로 칠한다 → 우측·하단에 흰 줄이 보인다.
    // 표지처럼 재단선까지 그림이 꽉 찬 페이지에서 특히 눈에 띈다.
    // 정수로 맞춘 뒤 그 크기에 정확히 맞는 뷰포트로 렌더해 남는 픽셀 자체를 없앤다.
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(vp.width);
    canvas.height = Math.round(vp.height);
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;

    const renderVp = page.getViewport({ scale: (canvas.width / vp1.width) * 1 });
    const task = page.render({ canvas, viewport: renderVp });
    activeRenderTasks.push(task);
    try {
      await task.promise;
    } catch {
      // 취소(RenderingCancelledException) 또는 토큰 무효 → 폐기
      page.cleanup();
      return null;
    } finally {
      activeRenderTasks = activeRenderTasks.filter((t) => t !== task);
    }
    if (myToken !== token) {
      page.cleanup();
      return null;
    }

    const dataUrl = canvas.toDataURL('image/png');

    // 썸네일(저해상도) — 본문 canvas 를 축소 복사
    const tw = thumbWidth;
    const thh = Math.round((tw / vp1.width) * vp1.height);
    const tcanvas = document.createElement('canvas');
    tcanvas.width = tw;
    tcanvas.height = thh;
    const tctx = tcanvas.getContext('2d');
    let thumbUrl = dataUrl;
    if (tctx) {
      tctx.drawImage(canvas, 0, 0, tw, thh);
      thumbUrl = tcanvas.toDataURL('image/png');
    }

    // 본문 canvas 해제(메모리)
    canvas.width = 0;
    canvas.height = 0;
    page.cleanup();

    return { index: pageNumber - 1, pageNumber, dataUrl, thumbUrl, baseW, baseH };
  }

  /**
   * PDF 를 순차 렌더한다. 새 호출은 이전 로드를 토큰으로 무효화하고 문서를 정리한다.
   * @param url         public 기준 PDF 경로
   * @param cols        1 | 2 단 보기 — 페이지 기준 폭 결정
   * @param targetWidth 실제 렌더 폭(px) — 지정 시 cols 기본 상수를 대신한다
   * @param maxPages    렌더할 최대 페이지 수 — 표지 썸네일처럼 1장만 필요할 때 쓴다.
   *                    (지정하지 않으면 전 페이지. `totalPages` 는 잘라내기 전 실제 값을 유지한다.)
   */
  async function load(
    url: string,
    cols: number,
    targetWidth?: number,
    maxPages?: number,
  ): Promise<void> {
    const myToken = ++token;
    await destroyActiveDoc();
    if (myToken !== token) return; // 정리 중 더 새 호출이 들어옴

    loading.value = true;
    error.value = null;
    pages.value = [];
    totalPages.value = null;
    currentPage.value = 1;

    try {
      const pdfjsLib = await loadPdfjs();
      if (myToken !== token) return; // 모듈 로드 대기 중 새 호출이 들어옴
      const loadingTask = pdfjsLib.getDocument({ url });
      activeTask = loadingTask;
      const doc = await loadingTask.promise;
      if (myToken !== token) {
        await loadingTask.destroy();
        return;
      }
      totalPages.value = doc.numPages;

      // 렌더 대상 수 — maxPages 는 상한일 뿐이라 totalPages(실제 장수)는 위에서 그대로 둔다.
      const renderCount = Math.min(doc.numPages, maxPages ?? doc.numPages);

      const rendered: PdfRenderedPage[] = [];
      for (let n = 1; n <= renderCount; n += 1) {
        const result = await renderPage(doc, n, cols, myToken, targetWidth);
        if (myToken !== token) return; // 취소됨
        if (result) {
          rendered.push(result);
          // 점진 노출: 페이지가 준비되는 대로 화면에 추가
          pages.value = [...rendered];
        }
      }
      if (myToken !== token) return;
      loading.value = false;
    } catch (err) {
      if (myToken !== token) return;
      loading.value = false;
      error.value = err instanceof Error ? err.message : String(err);
    }
  }

  /**
   * 렌더된 페이지 DOM 요소들에 IntersectionObserver 를 붙여 현재 페이지를 추적.
   * @param root      스크롤 컨테이너
   * @param elements  data-i(0-base) 를 가진 페이지 래퍼들
   */
  function observePages(root: HTMLElement, elements: HTMLElement[]): void {
    disposeObserver();
    if (!elements.length) return;
    observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            const i = Number((entry.target as HTMLElement).dataset.i ?? '0');
            currentPage.value = i + 1;
          }
        }
      },
      { root, threshold: 0.5 },
    );
    for (const el of elements) observer.observe(el);
  }

  /** 전체 정리 — 라우트 이탈/언마운트 시 호출(누수 0) */
  async function cleanup(): Promise<void> {
    token += 1; // 진행 중 로드 무효화
    loading.value = false;
    pages.value = [];
    await destroyActiveDoc();
  }

  onBeforeUnmount(() => {
    void cleanup();
  });

  return {
    pages,
    loading,
    error,
    totalPages,
    currentPage,
    load,
    observePages,
    cleanup,
  };
}
